import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { summarizeSkillLevelFulfillment } from '@/lib/skill-level-comparison';
import type { SkillMatrixRow, SkillMatrixMasterLevel } from '@/hooks/useSkillMatrixRows';

/**
 * `SkillMatrixEditor.tsx`・`SkillMatrixSelfViewTable.tsx` 共通の充足サマリー・到達基準文・
 * 充足/不足バッジ (docs/design/v05-skill-registration-table-redesign-detail.md §8-2)。
 * 実装後レビュー LOW是正 (QA、2026-09-20): 2ファイルへ完全重複していたものを共有化
 * (§8-152型: 片方だけ修正して表示がずれる事故の予防)。
 */
export function SkillFulfillmentSummary({ rows, skillLevelsMaster }: { rows: SkillMatrixRow[]; skillLevelsMaster: SkillMatrixMasterLevel[] }) {
  const { total, fulfilled } = summarizeSkillLevelFulfillment(rows, skillLevelsMaster);
  if (total === 0) return null;
  const pct = Math.round((fulfilled / total) * 100);
  return (
    <div className="border border-surface-border rounded-lg bg-white p-3">
      <div className="flex items-center justify-between gap-2 text-sm flex-wrap">
        <span className="text-text-body font-semibold">選択中の人材像が求めるスキル</span>
        <span className="text-text-body font-semibold tabular-nums">{fulfilled}/{total} 充足</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-surface-sunken mt-2 overflow-hidden" role="progressbar"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="求められるスキルの充足率">
        <div className={`h-full ${pct >= 100 ? 'bg-success-border' : 'bg-brand-orange'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** 対応する水準の到達基準文を、バッジ/選択の直下に常時表示する。 */
export function CriteriaText({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="text-[11px] text-text-sub mt-1 leading-snug break-words max-w-[240px]">{text}</p>;
}

export function FulfillmentIndicator({ fulfilled }: { fulfilled: boolean }) {
  return fulfilled ? (
    <span className="inline-flex items-center gap-0.5 text-[11px] text-success-text shrink-0">
      <CheckCircle2 size={12} aria-hidden />充足
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 text-[11px] text-alert-text shrink-0">
      <AlertTriangle size={12} aria-hidden />不足
    </span>
  );
}
